import React, { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import classNames from 'classnames'

import { SiteState } from '../store/site-reducer'
import { GraphQLState } from '../store/graphql-reducer'
import { SupportState } from '../store/support-reducer'
import navData from './navigation.json'
import Heading from './heading'
import icons from '../theme/icons'
import colors from '../theme/colors'
import styles from '../component-styles/desktop-nav.module.scss'
import UplifterSvg from '../svgs/uplifter'
import { getUpdateRequestsAsync } from '../store/actions'
import { BulletCounter, BetaLabel } from './counter'

interface NavItemProps {
  href?: string
  children?: React.ReactNode
  style?: object
  dataName?: string
  items?: any
  counter?: number | null
}

export function NavItem({
  href,
  children,
  dataName,
  style,
  items,
  counter = null,
}: NavItemProps): React.ReactElement {
  if (!href || (items && items.length > 0)) {
    return (
      <div className={styles.navItem} data-name={dataName} {...{ style }}>
        {children}
        {counter !== null && <BulletCounter count={counter} />}
      </div>
    )
  }

  return (
    <Link
      className={styles.navItem}
      to={href}
      data-name={dataName}
      {...{ style }}
    >
      {children}
      {counter !== null && (
        <BulletCounter count={counter} className={styles.navItemCounter} />
      )}
    </Link>
  )
}

interface SubNavProps {
  slug: string
  items?: any
  userPermission?: string | null
  counter?: number | null
}

export function SubNav({
  items,
  slug,
  userPermission = null,
  counter = null,
}: SubNavProps): React.ReactElement | null {
  if (!items || items.length <= 0) return null
  return (
    <div
      className={styles.subNav}
      style={{
        color: colors[slug],
      }}
    >
      {items.map((child: any): React.ReactElement | null => {
        if (
          userPermission &&
          Object.prototype.hasOwnProperty.call(child, 'permissions')
        ) {
          if (child.permissions.indexOf(userPermission) === -1) {
            return null
          }
        }
        const { beta } = child
        return (
          <Link
            key={child.slug}
            className={styles.subNavItem}
            to={child.href}
            data-name={`sub-${slug}`}
          >
            {child.name}
            {counter !== null && child.slug === 'track/edit-campaign-codes' && (
              <BulletCounter count={counter} className={styles.counter} />
            )}
            {beta && <BetaLabel />}
          </Link>
        )
      })}
    </div>
  )
}

interface DesktopNavProps {
  skeleton?: boolean
  justLogo?: boolean
  inSetup?: boolean
  grey?: boolean
  showWelcome?: boolean
  onboardingNotifications?: number
}

export default function DesktopNav({
  skeleton,
  justLogo,
  inSetup = false,
  grey = false,
  showWelcome = false,
  onboardingNotifications,
}: DesktopNavProps): React.ReactElement {
  const dispatch = useDispatch()

  const {
    graphql: { currentUser },
    site: { selected },
    support: { pendingRequests },
  }: {
    graphql: GraphQLState
    site: SiteState
    support: SupportState
  } = useSelector((s: any): any => s)

  const c = classNames({
    [styles.nav]: !justLogo,
    [styles.justLogo]: justLogo,
    [styles.greyNav]: grey,
  })

  useEffect(() => {
    dispatch(getUpdateRequestsAsync())
  }, [])

  const currentAccountPermission = currentUser?.currentAccountPermission || ''
  const requestCounter = useMemo(() => {
    if (pendingRequests && pendingRequests.length > 0) {
      if (
        currentAccountPermission &&
        (currentAccountPermission === 'support' ||
          currentAccountPermission === 'admin')
      ) {
        return pendingRequests.length
      }
    }
    return null
  }, [pendingRequests, currentAccountPermission])

  return (
    <nav className={c}>
      <Link to="/welcome">
        <UplifterSvg className={styles.logo} />
        {!!onboardingNotifications && (
          <BulletCounter
            count={onboardingNotifications}
            className={styles.onboardingCounter}
          />
        )}
      </Link>

      {!skeleton &&
        navData.map((item: any): React.ReactElement | null => {
          if (item.slug === 'welcome' && !showWelcome) {
            return <div key={item.slug} />
          }
          // While in setup more, only setting link can be present
          if (inSetup && item.slug !== 'settings' && item.slug !== 'track') {
            return (
              <NavItem
                href="/select-module"
                key={item.slug}
                dataName={item.slug}
                style={{
                  color: colors[item.slug],
                  fill: colors[item.slug],
                }}
              >
                <>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: icons[item.slug],
                    }}
                  />
                  <Heading type={3}>{item.name}</Heading>
                </>
              </NavItem>
            )
          }

          if (Object.prototype.hasOwnProperty.call(item, 'statusKey')) {
            if (!selected || selected[item.statusKey] === false) {
              return <div key={item.slug} />
            }
          }

          return (
            <NavItem
              href={item.href}
              key={item.slug}
              dataName={item.slug}
              items={item.children}
              counter={item.slug === 'track' ? requestCounter : null}
              style={{
                color: colors[item.slug],
                fill: colors[item.slug],
              }}
            >
              {item.href && item.children.length > 0 ? (
                <>
                  <Link
                    className={styles.navItem}
                    to={item.href}
                    data-name={item.slug}
                    style={{
                      color: colors[item.slug],
                      fill: colors[item.slug],
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: icons[item.slug],
                      }}
                    />
                    <Heading type={3}>{item.name}</Heading>
                  </Link>
                  <SubNav
                    counter={requestCounter}
                    slug={item.slug}
                    items={item.children}
                    userPermission={currentAccountPermission}
                  />
                </>
              ) : (
                <>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: icons[item.slug],
                    }}
                  />
                  <Heading type={3}>{item.name}</Heading>
                  <SubNav
                    counter={requestCounter}
                    slug={item.slug}
                    items={item.children}
                    userPermission={currentAccountPermission}
                  />
                </>
              )}
            </NavItem>
          )
        })}
    </nav>
  )
}
